import "babel-polyfill";

import Vue from "vue";

import { isClientUrl } from "@/utils/sourcing";
/*
 * This script bootstraps our Vue components.  Each Vue component is associated
 * with a DOM element name.  When a page has loaded, this script scans the DOM,
 * looking for elements of class "vue".  For each one:
 *
 * - it reads all of the data- attributes of the DOM element
 * - it creates an instance of the Vue component, initializing the properties
 *   using the names and values from the data- attributes, converted to the
 *   correct type
 * - then it mounts the component on the DOM element, replacing it
 *
 * Currently, you need to explicitly register each component.  You do this by:
 * - defining the mapping of element name to lazy component import (end of this script)
 * - optionally converting the types of the properties (Vue supports property
 *   types like Number and Boolean, whereas HTML attributes are always strings)
 * - converters can be defined in a "converters" function on the component.
 *
 * NOTE: Vue components support typed-properties (e.g. Numbers), but I can't
 * persuade Tapestry to write DOM element attributes without the surrounding
 * quotes, which turns them into Strings in Vue's eyes.
 *
 * If we can convince Tapestry to suppress the quotes (or use some other
 * server-side technology that does!), then we could throw away most of this
 * processing, in particular the converters and the need to scan for all "vue"
 * classes.  In turn, all 'class="vue"' references in our HTML files could also
 * be removed.
 *
 * Something to keep in the back of our minds...
 */

window.addEventListener("DOMContentLoaded", () => {
  /*
   * In our brave new Vue world, we link to existing Tapestry pages by ensuring
   * that the target page implements the activateExternalPage() method.
   *
   * The problem with this is that we sidestep the QLMSourcing mechanism that
   * maintains a stack of previously visited pages.  It might be possible to
   * add a service method that the browser can call, and ensure that the method
   * is called before allowing the browser to follow any links, but that seems
   * tricky.
   *
   * Instead, check the current URL: a URL created using our JavaScript client
   * code will have a special request parameter.  If it's a client URL, subvert
   * the standard back button behaviour: instead of deferring to Tapestry, ask
   * the browser to move back through the history.
   *
   * The code is implemented below.  Not sure it belongs here, but it's most
   * needed in the context of Vue components, so here it is.
   */
  if (isClientUrl(window.location.href)) {
    console.log("taking over back button for page linked from client code");
    const back = document.getElementById("goBackLink");
    if (back) back.href = "javascript:window.history.go(-1)";
  }

  /*
   * Given a component and a set of typed props, create a new Vue instance,
   * and instruct Vue to use the component and props to do the rendering.
   */
  function wrapComponent(component, props) {
    return new Vue({
      render(h) {
        return h(component, { props: props });
      }
    });
  }

  /*
   * Convert the values of the data- element attributes with typed equivalents,
   * as per rules specified by the component.
   *
   * "converters" is an object where each key is a prop name, and converter is
   * a function that converts from string to typed value.
   * If no converter is defined for a prop, then use the string value.
   */
  function convertProps(propsAsStrings, converters) {
    const result = { ...propsAsStrings };
    for (let [key, converter] of Object.entries(converters)) {
      result[key] = converter(result[key]);
    }
    return result;
  }

  /*
   * Find all DOM nodes with a class of "vue" and replace each one with an
   * instance of the appropriate Vue component.
   */
  function replaceVueElements() {
    const nodes = document.getElementsByClassName("vue");
    [...nodes].forEach(replaceVueElement);
  }

  /*
   * For a given element, lookup the component by element name, then replace
   * the element with an instance of the Vue-based component.
   */
  function replaceVueElement(el) {
    /*
     * In Chrome, at least, el.nodeName is in upper case.  Let's standardize on
     * lower case names throughout.
     */
    const nodeName = el.nodeName.toLowerCase();
    console.time(nodeName);

    const component = Vue.component(el.nodeName.toLowerCase());
    if (!component) {
      throw `No component named '${nodeName}' is registered`;
    }

    /*
     * Each component may define a converters() function.  Use this where a
     * component wants to enforce properties of types other than String
     * (String is the only data type that can be passed as an HTML attribute).
     *
     * See the convertProps() function.
     */
    const converters = component.converters || {};
    const props = convertProps(el.dataset, converters);

    /*
     * Now that we have a component class and a set of properties, we can
     * create a Vue instance that renders itself by delegating to the component
     * with the appropriate properties.
     */
    const app = wrapComponent(component, props);

    /*
     * Finally, mount the Vue instance on the original DOM element.  This is
     * what replaces the original element with the Vue-based content.
     */
    app.$mount(el);

    console.timeEnd(nodeName);
  }

  /*
   * Register all top-level Vue components.
   *
   * NOTE: there may be a cleaner way of doing this.
   */
  Vue.component("approval-process", () =>
    import("@/views/ApprovalProcess/index.vue")
  );
  Vue.component("auto-data-table", () =>
    import("@/components/AutoDataTable.vue")
  );
  Vue.component("bulk-attach", () => import("@/views/BulkAttach.vue"));
  Vue.component("customer-quote", () =>
    import("@/views/CustomerQuote/index.vue")
  );
  Vue.component("dashboard", () => import("@/views/Dashboard.vue"));
  Vue.component("dashboard-admin", () => import("@/views/DashboardAdmin.vue"));
  Vue.component("data-collection", () =>
    import("@/views/DataCollection/index.vue")
  );
  Vue.component("display-kit-data", () =>
    import("@/views/DisplayKitData/index.vue")
  );
  Vue.component("favourites", () => import("@/components/Favourites.vue"));
  Vue.component("inbox-dashboards", () =>
    import("@/views/InboxDashboards.vue")
  );
  Vue.component("level-2-menu", () => import("@/components/Level2Menu.vue"));
  Vue.component("lookup-table", () => import("@/views/LookupTable/index.vue"));
  Vue.component("quote-analyzer", () =>
    import("@/views/QuoteAnalyzer/index.vue")
  );
  Vue.component("manual-quote", () => import("@/views/ManualQuote/index.vue"));
  Vue.component("quote-cube-line", () =>
    import("@/views/quote-cube/QuoteCubeLine.vue")
  );
  Vue.component("rfq-requisition-admin", () =>
    import("@/views/RfqRequisitionAdmin.vue")
  );
  Vue.component("split-lines", () => import("@/views/SplitLines/index.vue"));
  Vue.component("upload-table", () =>
    import("@/components/UploadTable/index.vue")
  );
  Vue.component("update-rfq-lines", () =>
    import("@/components/UpdateRfqLines.vue")
  );

  /*
   * For each component, register the CSS class, Vue component name,
   * and object defining property converters.
   */
  console.time("instantiating Vue components");
  replaceVueElements();
  console.timeEnd("instantiating Vue components");
});
